import { FC, Fragment, useCallback, useEffect, useState } from "react";

import { RightOutlined, LeftOutlined, FlagOutlined, LinkOutlined } from "@ant-design/icons";
import {
  Button,
  Checkbox,
  Col,
  DatePicker,
  Divider,
  Drawer,
  Flex,
  Form,
  Input,
  InputNumber,
  List,
  Modal,
  Row,
  Select,
  Tooltip,
  Typography,
  message,
} from "antd";
import fr from "antd/es/date-picker/locale/fr_FR";
import { isEmpty } from "lodash";
import { useLoaderData, useRevalidator, useSearchParams } from "react-router-dom";
import { Link } from "react-router-dom";

import DuplicateTransactions from "./actions/DuplicateTransactions";
import LinkToFao from "./actions/LinkToFao";
import RefreshAddress from "./actions/RefreshAddress";
import ValidateUpdates from "./actions/ValidateUpdates";
import { AddressSearchField, AddressFormValues } from "./layers/AddressManager";
import AvailabilityPicker from "./layers/AvailabilityPicker";
import MultiFileUpload from "./layers/MultiFileUpload";
import PhotoThumbnail from "./layers/PhotoThumbnail";
import {
  Field,
  FieldOption,
  GroupedFields,
  calculatedFields,
  getCollectionDefaultValues,
} from "./services/collectionFields";
import {
  createItem,
  deleteItem,
  ItemChanges,
  saveItemChanges,
  transformItem,
  refreshAddressFromParcelle,
} from "./services/collectionItems";
import axiosInstance from "./axiosInstance";
import dayjs from "dayjs";

const { TextArea } = Input;

interface EditCollectionProps {
  editItem: any;
  mode: "edit" | "create";
  onCloseEdit: () => void;
  onEditNext?: () => void;
  onEditPrevious?: () => void;
}

const EditCollection: FC<EditCollectionProps> = ({
  editItem,
  mode,
  onCloseEdit,
  onEditNext,
  onEditPrevious,
}) => {
  const [form] = Form.useForm();
  const [revisions, setRevisions] = useState<
    { timestamp: number; user: string }[]
  >([]);
  const [modal, contextHolder1] = Modal.useModal();
  const [messageApi, contextHolder2] = message.useMessage();
  const [changedValues, setChangedValues] = useState<ItemChanges>({});
  const [isSaving, setIsSaving] = useState(false);
  const revalidator = useRevalidator();
  const { collectionId, groupedFields, fields } = useLoaderData() as {
    collectionId: string;
    groupedFields: GroupedFields;
    fields: Field[];
  };
  const [editedItem, setEditedItem] = useState<any>(editItem);

  const isEditMode = mode === "edit";
  const [searchParams] = useSearchParams();
  const isIframeMode = searchParams.get('iframe') === 'true';

  // Handle address selection for biens_off_market
  const handleAddressSelection = (updatedValues: Partial<AddressFormValues>) => {
    form.setFieldsValue(updatedValues);
    setChangedValues(prev => ({ ...prev, ...updatedValues }));
  };

  // Update address from NoCommune and NoParcelle for biens_off_market
  const handleUpdateAddressFromParcelle = async () => {
    if (!editedItem?.id) {
      messageApi.open({
        type: "warning",
        content: "Aucun élément sélectionné.",
      });
      return;
    }

    if (!editedItem?.NoCommune || !editedItem?.NoParcelle) {
      messageApi.open({
        type: "warning",
        content: "NoCommune et NoParcelle sont requis pour mettre à jour l'adresse.",
      });
      return;
    }

    try {
      messageApi.open({
        type: "info",
        content: "Mise à jour de l'adresse en cours...",
        duration: 2,
      });

      // Call backend endpoint to update address
      await refreshAddressFromParcelle(editedItem.id.toString());

      // Reload the item to get updated values
      const response = await axiosInstance.get(
        `https://melcarnedirectus2.digipulse.ch/items/biens_off_market/${editedItem.id}`
      );
      const updatedItem = transformItem(response.data.data, fields);
      setEditedItem(updatedItem);
      form.setFieldsValue(updatedItem);
      setChangedValues({});

      messageApi.open({
        type: "success",
        content: "L'adresse a été mise à jour avec succès.",
      });
    } catch (error: any) {
      console.error("Error updating address from parcelle:", error);
      messageApi.open({
        type: "error",
        content: error.response?.data?.error || "Une erreur est survenue lors de la mise à jour de l'adresse.",
      });
    }
  };

  useEffect(() => {
    // Initialize editedItem for iframe create mode
    if (isIframeMode && mode === "create" && !editItem) {
      setEditedItem({});
    } else {
      setEditedItem(editItem);
    }
  }, [editItem, isIframeMode, mode]);

  useEffect(() => {
    if (!editedItem && !(isIframeMode && mode === "create")) return;

    form.setFieldsValue(editedItem || {});
  }, [editedItem, form, isIframeMode, mode]);

  // Load default values for new items
  useEffect(() => {
    const loadDefaultValues = async () => {
      if (mode === "create" && (editedItem && Object.keys(editedItem).length === 0)) {
        try {
          const defaultValues = await getCollectionDefaultValues(collectionId);
          if (Object.keys(defaultValues).length > 0) {
            form.setFieldsValue(defaultValues);
            setChangedValues(defaultValues);
          }
        } catch (error) {
          console.error("Failed to load default values:", error);
        }
      }
    };

    loadDefaultValues();
  }, [mode, editedItem, collectionId, form]);

  // Add URL parameter support for pre-filling in create mode
  useEffect(() => {
    if (mode === "create" && isIframeMode) {
      // Delay the parameter processing to ensure form is initialized
      const timer = setTimeout(() => {
        const urlParams: Record<string, any> = {};
        
        // Extract all URL parameters that match field names
        searchParams.forEach((value, key) => {
          // Skip navigation/system parameters
          if (['iframe', 'mode'].includes(key)) {
            return;
          }
          
          // Handle special cases for JSON fields
          if (key === 'Position_sur_map') {
            try {
              urlParams[key] = JSON.parse(value);
            } catch (e) {
              // If JSON parsing fails, skip this parameter
              return;
            }
          } else {
            urlParams[key] = value;
          }
        });
        
        if (Object.keys(urlParams).length > 0) {
          form.setFieldsValue(urlParams);
          setChangedValues(urlParams);
        }
      }, 200); // Increased delay to ensure form is fully rendered
      
      return () => clearTimeout(timer);
    }
  }, [mode, isIframeMode, searchParams, form]);

  useEffect(() => {
    if (!editedItem?.id || mode === "create") return;
    
    console.log(`Fetching revisions for collection: ${collectionId}, item_id: ${editedItem.id}`);
    
    fetch(
      `https://melcarnebackend.digipulse.ch/get_revisions?collection_name=${collectionId}&item_id=${editedItem.id}`,
      { cache: 'no-store' }
    )
      .then((res) => res.json())
      .then((data) => {
        console.log(`Received ${data.length} revisions for ${collectionId}:${editedItem.id}`);
        setRevisions(data);
      })
      .catch((error) => {
        console.error(`Error fetching revisions for ${collectionId}:${editedItem.id}`, error);
      });
  }, [editedItem?.id, collectionId, mode]);


  const handleValuesChange = useCallback(
    async (updatedValues: any) => {      
        const transformedValues = Object.entries(updatedValues).reduce((acc: Record<string, any>, [key, value]) => {
          const field = fields.find(f => f.name === key);
          
          if (field && (field.type === 'date' || field.type === 'timestamp') && value && dayjs.isDayjs(value)) {
            return {
              ...acc,
              [key]: value.format('YYYY-MM-DD')
            };
          }
          
          return {
            ...acc,
            [key]: value
          };
        }, {});

        setChangedValues((values) => ({
          ...values,
          ...transformedValues,
        }));

        try {
          const calcFn = calculatedFields[collectionId];
          if (calcFn) {
            const calculationResult = await calcFn(
              Object.keys(updatedValues)[0],
              { ...editedItem, ...changedValues, ...transformedValues }
            );
            if (calculationResult && Object.keys(calculationResult).length > 0) {
              form.setFieldsValue(calculationResult);
              setChangedValues((values) => ({
                ...values,
                ...calculationResult,
              }));
            }
          }
        } catch (error) {
          console.error("Error computing calculated fields:", error);
        }
    },
    [editedItem, changedValues, setChangedValues, form, fields, collectionId]
  );

  const handleClose = () => {
    setChangedValues({});
    onCloseEdit();
  };

  const handleSaveItemChanges = async () => {
    if (isSaving) return;

    if (!isEmpty(changedValues)) {
      setIsSaving(true);
      // Once the item has an id (even in create mode after a first save),
      // we must update it instead of creating a duplicate record.
      const isUpdate = isEditMode || !!editedItem?.id;
      try {
        let itemResponse;
        if (isUpdate) {
          itemResponse = await saveItemChanges(
            editedItem.id,
            collectionId,
            changedValues
          );
        } else {
          itemResponse = await createItem(collectionId, changedValues);
        }

        setEditedItem(transformItem(itemResponse.data.data, fields));

        messageApi.open({
          type: "success",
          content: isUpdate
            ? "La mise à jour a été effectuée avec succès."
            : "La transaction à jour a été créée avec succès.",
        });

        setChangedValues({});

        // Refresh the collection table so the new/updated record shows up
        revalidator.revalidate();

        // In iframe mode, send message to parent to close modal
        if (isIframeMode && window.parent !== window) {
          window.parent.postMessage({ type: 'close-modal' }, '*');
        }
      } catch (error) {
        console.error(error);
        messageApi.open({
          type: "error",
          content: isUpdate
            ? "Une erreur est survenue lors de la mise à jour."
            : "Une erreur est survenue lors de la création.",
        });
      } finally {
        setIsSaving(false);
      }
    }
  };

  const handleAskSaveItemChanges = async () => {
    if (!isEmpty(changedValues)) {
      const confirmed = await modal.confirm({
        title: "Voulez-vous sauvegarder les changements en cours?",
        okText: "Oui",
        cancelText: "Non",
      });

      if (confirmed) {
        await handleSaveItemChanges();
      }
    }
  };

  const handleDeleteItem = async () => {
    if (!editedItem?.id) return;

    const confirmed = await modal.confirm({
      title: "Êtes-vous sûr de vouloir supprimer cet élément?",
      content: "Cette action est irréversible.",
      okText: "Supprimer",
      cancelText: "Annuler",
      okType: "danger",
    });

    if (confirmed) {
      try {
        await deleteItem(editedItem.id, collectionId);
        
        messageApi.open({
          type: "success",
          content: "L'élément a été supprimé avec succès.",
        });

        // Close the drawer after successful deletion
        handleClose();
        
        // Refresh the page to update the list
        window.location.reload();
      } catch (error) {
        console.error(error);
        messageApi.open({
          type: "error",
          content: "Une erreur est survenue lors de la suppression.",
        });
      }
    }
  };

  // If in iframe mode, render form directly on page
  if (isIframeMode && mode === "create") {
    return (
      <div style={{ padding: '24px', height: '100%' }}>
        {contextHolder2}
        <div style={{ marginBottom: '24px' }}>
          <h1>Créer un bien off-market</h1>
        </div>
        
        <Form
          size="middle"
          form={form}
          layout="vertical"
          autoComplete="off"
          initialValues={editedItem || {}}
          onValuesChange={handleValuesChange}
        >
          {/* Address search for biens_off_market collection */}
          {collectionId === "biens_off_market" && (
            <AddressSearchField 
              span={24} 
              onAddressSelected={handleAddressSelection} 
            />
          )}
          
          {Object.keys(groupedFields)
            .sort((g1, g2) => (g1 > g2 ? 1 : -1))
            .map((group: string) => {
              return (
                <Fragment key={group}>
                  {group && <Divider style={{ margin: "8px 0" }}></Divider>}
                  <Row gutter={[16, 16]}>
                    {groupedFields[group]
                      .filter((field) => !field.configuration.hideInput)
                      .map((field: Field) => {
                        // MultiFileUpload and PhotoThumbnail get full width in iframe mode too
                        const colSpan = (field.name === "MultiFileUpload" || field.name === "PhotoThumbnail") ? 24 : 12;
                        return (
                          <Col key={field.name} span={colSpan}>
                            <Form.Item
                              label={field.label}
                              name={field.name}
                              valuePropName={
                                field.type === "boolean" ? "checked" : "value"
                              }
                            >
                              {(field.configuration?.isLink && (field.type === "integer" || field.type === "float")) ? (
                                <div style={{ position: "relative" }}>
                                  {getEditComponent(field, editedItem || {}, form, setChangedValues, handleSaveItemChanges, changedValues, isEditMode, collectionId, messageApi, fields, setEditedItem, () => revalidator.revalidate())}
                                  {(changedValues[field.name] ?? editedItem?.[field.name]) && (
                                    <LinkOutlined
                                      style={{ 
                                        color: "#1890ff", 
                                        cursor: "pointer", 
                                        fontSize: 16,
                                        position: "absolute",
                                        right: 8,
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                        zIndex: 1
                                      }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const url = changedValues[field.name] ?? editedItem?.[field.name];
                                        if (url) {
                                          window.open(url, "_blank", "noopener,noreferrer");
                                        }
                                      }}
                                    />
                                  )}
                                </div>
                              ) : (
                                getEditComponent(field, editedItem || {}, form, setChangedValues, handleSaveItemChanges, changedValues, isEditMode, collectionId, messageApi, fields, setEditedItem, () => revalidator.revalidate())
                              )}
                            </Form.Item>
                          </Col>
                        );
                      })}
                  </Row>
                </Fragment>
              );
            })}
        </Form>
        
        <div style={{ 
          marginTop: '32px',
          padding: '16px 0',
          borderTop: '1px solid #f0f0f0',
          backgroundColor: 'white',
          position: 'sticky',
          bottom: 0,
          zIndex: 10
        }}>
          <Flex gap="middle" justify="end">
            <Button onClick={handleClose}>Annuler</Button>
            <Button
              onClick={handleSaveItemChanges}
              type="primary"
              disabled={isEmpty(changedValues)}
            >
              Créer
            </Button>
          </Flex>
        </div>
      </div>
    );
  }

  return (
    <>
      {contextHolder2}
      <Drawer
        width="85%"
        title={
          isEditMode ? (
            <Flex
              style={{ paddingLeft: 12 }}
              justify="space-between"
              align="center"
            >
              {editedItem && (
                <>
                  <Typography.Text
                    strong
                    style={{ fontSize: 16 }}
                  >{collectionId === "recherches" ? " " : `${editedItem.NoParcelle} - ${editedItem.Requete} - ${editedItem.Adresse}, ${editedItem.NPA} ${editedItem.CommuneAdresse}`}</Typography.Text>
                  {(editedItem.qpt ||
                    editedItem.PrixZD ||
                    editedItem.PrixNonRepresentatif) && (
                    <Tooltip
                      title="Prix ignoré dû à qpt / PrixZd / Prix non représentatif"
                      placement="top"
                    >
                      <FlagOutlined
                        style={{
                          color: "red",
                          fontSize: 14,
                          marginLeft: 4,
                          verticalAlign: "middle",
                          cursor: "default",
                        }}
                        aria-label="Avertissement de prix"
                      />
                    </Tooltip>
                  )}
                </>
              )}
              <Flex gap={16}>
                {contextHolder1}
                <Button
                  icon={<LeftOutlined />}
                  iconPosition="start"
                  disabled={!onEditPrevious}
                  onClick={async () => {
                    await handleAskSaveItemChanges();

                    setChangedValues({});
                    onEditPrevious && onEditPrevious();
                  }}
                >
                  Previous
                </Button>
                <Button
                  icon={<RightOutlined />}
                  iconPosition="end"
                  disabled={!onEditNext}
                  onClick={async () => {
                    await handleAskSaveItemChanges();

                    setChangedValues({});
                    onEditNext && onEditNext();
                  }}
                >
                  Next
                </Button>
              </Flex>
            </Flex>
          ) : (
            "Créer un enregistrement"
          )
        }
        open={!!editedItem || (isIframeMode && mode === "create")}
        onClose={handleClose}
        footer={
          <Flex justify="space-between">
            {editedItem && (
              <Flex gap="middle" align="center">
                {collectionId === "transactions_updates" && (
                  <ValidateUpdates ids={[editedItem.id]} />
                )}

                {["transactions_updates", "transactions_fao"].includes(
                  collectionId
                ) && isEditMode ? (
                  <>
                    <RefreshAddress ids={[editedItem.id]} />
                    <DuplicateTransactions ids={[editedItem.id]} />
                  </>
                ) : (
                  <div />
                )}

                {collectionId === "biens_off_market" && isEditMode && (
                  <Button
                    onClick={handleUpdateAddressFromParcelle}
                    style={{ backgroundColor: "#4caf50" }}
                  >
                    Mettre à jour l'adresse depuis la parcelle
                  </Button>
                )}

                {collectionId === "transactions_updates" && (
                  <LinkToFao
                    requete={editedItem.Requete.split("/")
                      .slice(0, 2)
                      .join("/")}
                  />
                )}
              </Flex>
            )}
            <Flex gap="middle">
              {[
                "biens_off_market",
                "recherches",
                "transactions_updates",
                "transactions_fao",
                "registre_foncier",
                "sms_challenges",
              ].includes(collectionId) && (
                <>
                  {isEditMode && [
                    "biens_off_market",
                    "recherches",
                    "registre_foncier",
                    "sms_challenges",
                  ].includes(collectionId) && (
                    <Button 
                      onClick={handleDeleteItem}
                      danger
                    >
                      Supprimer
                    </Button>
                  )}
                  <Button onClick={handleClose}>Annuler</Button>
                  <Button
                    onClick={handleSaveItemChanges}
                    type="primary"
                    disabled={isEmpty(changedValues)}
                  >
                    {isEditMode ? "Sauvegarder" : "Créer"}
                  </Button>
                </>
              )}
            </Flex>
          </Flex>
        }
      >
        {(editedItem || (isIframeMode && mode === "create")) && (
          <Form
            size="middle"
            form={form}
            layout="vertical"
            autoComplete="off"
            initialValues={editedItem}
            onValuesChange={handleValuesChange}
          >
            {/* Address search for biens_off_market collection */}
            {collectionId === "biens_off_market" && (
              <Row gutter={24}>
                <Col sm={12} md={8} lg={8} xl={6}>
                  <AddressSearchField 
                    span={24} 
                    onAddressSelected={handleAddressSelection} 
                  />
                </Col>
              </Row>
            )}
            
            {Object.keys(groupedFields)
              .sort((g1, g2) => (g1 > g2 ? 1 : -1))
              .map((group: string) => {
                return (
                  <Fragment key={group}>
                    {group && <Divider style={{ margin: "8px 0" }}></Divider>}

                    <Row gutter={24}>
                    {groupedFields[group]
                      .sort((f1, f2) => f1.sort - f2.sort)
                      .map((field: Field) => {
                        const { name, label, type } = field;
                        // MultiFileUpload and PhotoThumbnail get full width
                        const isFullWidth = name === "MultiFileUpload" || name === "PhotoThumbnail";
                        return (
                          <Col 
                            key={name} 
                            span={isFullWidth ? 24 : undefined}
                            sm={isFullWidth ? undefined : 12}
                            md={isFullWidth ? undefined : 8}
                            lg={isFullWidth ? undefined : 8}
                            xl={isFullWidth ? undefined : 6}
                          >
                            <Form.Item
                              key={name}
                              name={name}
                              label={label}
                              style={{
                                marginBottom: 16,
                              }}
                              validateStatus={
                                changedValues[name] ? "warning" : ""
                              }
                              valuePropName={
                                type === "boolean" ? "checked" : "value"
                              }
                            >
                              {(field.configuration?.isLink && (type === "integer" || type === "float")) ? (
                                <div style={{ position: "relative" }}>
                                  {getEditComponent(field, editedItem, form, setChangedValues, handleSaveItemChanges, changedValues, isEditMode, collectionId, messageApi, fields, setEditedItem, () => revalidator.revalidate())}
                                  {(changedValues[name] ?? editedItem?.[name]) && (
                                    <LinkOutlined
                                      style={{ 
                                        color: "#1890ff", 
                                        cursor: "pointer", 
                                        fontSize: 16,
                                        position: "absolute",
                                        right: 8,
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                        zIndex: 1
                                      }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const url = changedValues[name] ?? editedItem?.[name];
                                        if (url) {
                                          window.open(url, "_blank", "noopener,noreferrer");
                                        }
                                      }}
                                    />
                                  )}
                                </div>
                              ) : (
                                getEditComponent(field, editedItem, form, setChangedValues, handleSaveItemChanges, changedValues, isEditMode, collectionId, messageApi, fields, setEditedItem, () => revalidator.revalidate())
                              )}
                            </Form.Item>
                          </Col>
                        );
                      })}
                    </Row>
                  </Fragment>
                );
              })}
          </Form>
        )}

        {revisions.length > 0 && isEditMode && (
          <>
            <Divider style={{ margin: "24px 0 8px" }}>
              Historique des révisions
            </Divider>
            <List
              dataSource={revisions}
              renderItem={(rev) => (
                <List.Item>
                  <List.Item.Meta
                    title={dayjs(rev.timestamp).format("DD-MM-YYYY HH:mm:ss")}
                    description={rev.user}
                  />
                </List.Item>
              )}
            />
          </>
        )}
      </Drawer>
    </>
  );
};

export default EditCollection;

const getEditComponent = (
  field: Field, 
  editedItem: any, 
  form?: any, 
  setChangedValues?: (updater: (prev: any) => any) => void,
  handleSaveItemChanges?: () => void,
  changedValues?: any,
  isEditMode?: boolean,
  collectionId?: string,
  messageApi?: any,
  fields?: Field[],
  setEditedItem?: (item: any) => void,
  onSaved?: () => void
) => {
  const { ui, type, name, options, configuration } = field;
  const isFieldHighlighted = configuration?.highlighted;
  const isGreenOnFilled = configuration?.greenOnFilled;
  const isLink = configuration?.isLink;
  const fieldValue = editedItem?.[name];
  const hasValue = fieldValue !== null && fieldValue !== undefined && fieldValue !== "";
  
  let backgroundColor = "inherit";
  if (isFieldHighlighted) {
    backgroundColor = "yellow";
  } else if (isGreenOnFilled && hasValue) {
    backgroundColor = "lightgreen";
  }
  
  // Add padding for link icon
  const linkPadding = isLink ? { paddingRight: 32 } : {};

  //console.log(field);
  //console.log(editedItem[name]);
    // Handle MultiFileUpload by field name
    if (name === "MultiFileUpload") {
      return (
        <MultiFileUpload
          disabled={configuration?.hideInput}
          value={editedItem[name]}
          onChange={(value) => {
            form?.setFieldValue(name, value);
            const newChangedValues = { ...(changedValues || {}), [name]: value };
            setChangedValues?.(() => newChangedValues);
            
            // Trigger auto-save with the new values directly
            if (handleSaveItemChanges && !isEmpty(newChangedValues) && collectionId) {
              setTimeout(async () => {
                try {
                  // If the item already has an id (even in create mode after a
                  // first save), update it instead of creating a duplicate.
                  const hasId = !!editedItem?.id;
                  let itemResponse;
                  if (hasId) {
                    itemResponse = await saveItemChanges(
                      editedItem.id,
                      collectionId,
                      newChangedValues
                    );
                  } else {
                    itemResponse = await createItem(collectionId, newChangedValues);
                  }

                  if (itemResponse) {
                    if (fields) {
                      setEditedItem?.(transformItem(itemResponse.data.data, fields));
                    }
                    messageApi.success({
                      content: hasId ? "Changements sauvegardés" : "Élément créé avec succès",
                      duration: 2,
                    });
                    
                    // Clear the changed values after successful save
                    setChangedValues?.(() => ({}));

                    // Refresh the collection table
                    onSaved?.();
                  }
                } catch (error) {
                  console.error('Auto-save error:', error);
                  messageApi.error("Erreur lors de la sauvegarde");
                }
              }, 50);
            }
          }}
        />
      );
    }

    // Handle PhotoThumbnail by field name
    if (name === "PhotoThumbnail") {
      return (
        <PhotoThumbnail
          disabled={configuration?.hideInput}
          value={editedItem[name]}
          onChange={(value) => {
            form?.setFieldValue(name, value);
            const newChangedValues = { ...(changedValues || {}), [name]: value };
            setChangedValues?.(() => newChangedValues);
            
            // Trigger auto-save with the new values directly
            if (handleSaveItemChanges && !isEmpty(newChangedValues) && collectionId) {
              setTimeout(async () => {
                try {
                  // If the item already has an id (even in create mode after a
                  // first save), update it instead of creating a duplicate.
                  const hasId = !!editedItem?.id;
                  let itemResponse;
                  if (hasId) {
                    itemResponse = await saveItemChanges(
                      editedItem.id,
                      collectionId,
                      newChangedValues
                    );
                  } else {
                    itemResponse = await createItem(collectionId, newChangedValues);
                  }

                  if (itemResponse) {
                    if (fields) {
                      setEditedItem?.(transformItem(itemResponse.data.data, fields));
                    }
                    messageApi.success({
                      content: hasId ? "Changements sauvegardés" : "Élément créé avec succès",
                      duration: 2,
                    });
                    
                    // Clear the changed values after successful save
                    setChangedValues?.(() => ({}));

                    // Refresh the collection table
                    onSaved?.();
                  }
                } catch (error) {
                  console.error('Auto-save error:', error);
                  messageApi.error("Erreur lors de la sauvegarde");
                }
              }, 50);
            }
          }}
        />
      );
    }

  if (configuration?.availabilityPicker) {
    return <AvailabilityPicker style={{ width: "100%", backgroundColor }} />;
  }

  // DEBUG: remove after testing
  if (name.toLowerCase().includes("disponib")) {
    console.log("DEBUG disponibilite field:", JSON.stringify(configuration));
  }

  switch (ui) {
    case "datetime":
      return (
        <DatePicker
          style={{ width: "100%", backgroundColor }}
          locale={fr}
          format="YYYY-MM-DD"
        />
      );

    case "input-multiline":
      return (
        <TextArea
          style={{ backgroundColor }}
          rows={6}
        />
      );

    case "select-dropdown":
      const selectClassName = (() => {
        if (isFieldHighlighted) {
          return "highlighted";
        }
        if (isGreenOnFilled && hasValue) {
          return "green-filled";
        }
        return "";
      })();
      
      return (
        <Select
          className={selectClassName}
          style={{ width: "100%" }}
        >
          {(options || []).map((option: FieldOption) => (
            <Select.Option key={option.value} value={option.value}>
              {option.label}
            </Select.Option>
          ))}
        </Select>
      );

    case "input":
      if (type === "integer") {
        return (
          <InputNumber<number>
            style={{
              width: "100%",
              backgroundColor,
              ...linkPadding,
            }}
            step={1}
            type="number"
          />
        );
      }

      if (type === "float" || type === "decimal") {
        return (
          <InputNumber<number>
            style={{
              width: "100%",
              backgroundColor,
              ...linkPadding,
            }}
            step={0.01}
            precision={2}
          />
        );
      }

      const currentValue = changedValues?.[name] ?? editedItem?.[name];
      return (
        <Input
          style={{ width: "100%", backgroundColor, ...linkPadding }}
          suffix={isLink && currentValue ? (
            <LinkOutlined
              style={{ color: "#1890ff", cursor: "pointer" }}
              onClick={(e) => {
                e.stopPropagation();
                if (currentValue) {
                  window.open(currentValue, "_blank", "noopener,noreferrer");
                }
              }}
            />
          ) : undefined}
        />
      );

    case "map":
      if (editedItem?.[name]) {
        const { coordinates } = editedItem[name];
        const lat = coordinates[1];
        const long = coordinates[0];
        return (
          <div style={{ display: "flex", flexDirection: "column" }}>
            <Link
              target="_blank"
              to={`https://www.google.ch/maps/place/${lat},${long}`}
            >
              <Typography.Text
                style={{ color: "#32499c" }}
              >Google Maps</Typography.Text>
            </Link>
            <Link
              target="_blank"
              to={`https://map.sitg.ge.ch/app/?locparcelle=${editedItem?.NoCommune}:${editedItem?.NoParcelle}&mapresources=AMENAGEMENT`}
            >
              <Typography.Text style={{ color: "#32499c" }}>
                SITG
              </Typography.Text>
            </Link>
            <Link
              target="_blank"
              to={`https://ge.ch/terextraitfoncier/rapport.aspx?commune=${editedItem?.NoCommune}&parcelle=${editedItem?.NoParcelle}`}
            >
              <Typography.Text style={{ color: "#32499c" }}>
                Extrait foncier
              </Typography.Text>
            </Link>
            {editedItem?.Egrid && (
              <Link
                target="_blank"
                to={`https://ge.ch/terecadastrews/RdppfSVC.svc/extract/pdf/?EGRID=${editedItem?.Egrid}`}
              >
                <Typography.Text style={{ color: "#32499c" }}>
                  RDPPF
                </Typography.Text>
              </Link>
            )}
            {editedItem?.Egrid && (
            <Link
              target="_blank"
              to={`https://madd.bfs.admin.ch/search_datatable?geb_gegrid=${editedItem?.Egrid}`}
            >
              <Typography.Text style={{ color: "#32499c" }}>
                  RegBL (admin.ch)
                </Typography.Text>
              </Link>
            )}
            {/*
            <Link
              target="_blank"
              to={`https://melcarnebackend.digipulse.ch/search_geneva_building_permits?transaction_id=${editedItem?.id}`}
            >
              <Typography.Text style={{ color: "#32499c" }}>
                SAD
              </Typography.Text>
            </Link>
            */}
          </div>
        );
      }
      return <div></div>;

    case "numeric":
      return (
        <InputNumber<number>
          style={{
            width: "100%",
            backgroundColor,
            ...linkPadding,
          }}
          step={type === "integer" ? 1 : 0.01}
          precision={type === "integer" ? 0 : 2}
        />
      );


    case "boolean":
      const checkboxClassName = (() => {
        if (isFieldHighlighted && !editedItem?.[name]) {
          return "highlighted";
        }
        if (isGreenOnFilled && hasValue) {
          return "green-filled";
        }
        return "";
      })();
      
      return (
        <Checkbox
          className={checkboxClassName}
        />
      );

    case "input-multiline":
      return (
        <TextArea
          style={{ backgroundColor }}
          rows={6}
        />
      );

    default:
      return (
        <Input
          style={{ width: "100%", backgroundColor }}
        />
      );
  }
};
